import React, { useState, useEffect } from 'react';
import { 
  X, 
  Download, 
  CheckCircle, 
  Star,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Users
} from 'lucide-react';
import './BrandFeedbackModal.css';
import toast from 'react-hot-toast';

const ApprovedContentModal = ({ 
  isOpen, 
  onClose, 
  submission, 
  onLeaveReview,
  onReassignCreator
}) => {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    review: ''
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [hasReview, setHasReview] = useState(false); // Track if review has been submitted

  // Check if submission already has a review when it loads
  useEffect(() => {
    if (submission) {
      const submissionHasReview = submission.brandReview || submission._originalData?.brandReview;
      setHasReview(!!submissionHasReview);
      
      // Set initial video index if provided
      if (submission._initialVideoIndex !== undefined) {
        setCurrentVideoIndex(submission._initialVideoIndex);
      }
    }
  }, [submission]);

  if (!isOpen || !submission) return null;

  // Check if submission already has a review (from props or state)
  const submissionHasReview = hasReview || submission.brandReview || submission._originalData?.brandReview;

  const videos = submission.content || [];
  const currentVideo = videos[currentVideoIndex];

  // Debug: Log video data to see what's available
  console.log('Current video data:', currentVideo);
  console.log('Submission data:', submission);

  const handlePreviousVideo = () => {
    setCurrentVideoIndex(prev => prev > 0 ? prev - 1 : videos.length - 1);
  };

  const handleNextVideo = () => {
    setCurrentVideoIndex(prev => prev < videos.length - 1 ? prev + 1 : 0);
  };

  const handleDownloadAll = async () => {
    try {
      // Download all videos as MP4
      for (const video of submission.content) {
        const url = video.downloadURL || video.videoUrl;
        if (url) {
          const link = document.createElement('a');
          link.href = url;
          link.download = `${video.title || 'video'}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      toast.success('All videos downloaded successfully!');
    } catch (error) {
      console.error('Error downloading videos:', error);
      toast.error('Failed to download some videos');
    }
  };

  const handleDownloadIndividual = async (video) => {
    try {
      const url = video.downloadURL || video.videoUrl;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        
        // Use brief name and script name instead of creator's filename
        const briefName = submission.briefName || submission.briefTitle || 'brief';
        const scriptName = video.scriptName || video.scriptTitle || `script_${currentVideoIndex + 1}`;
        const fileName = `${briefName}_${scriptName}.mp4`;
        
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`${fileName} downloaded successfully!`);
      }
    } catch (error) {
      console.error('Error downloading video:', error);
      toast.error('Failed to download video');
    }
  };

  const handleReviewSubmit = async () => {
    setIsSubmittingReview(true);
    try {
      // Submit review data with full submission object for context
      await onLeaveReview(submission, reviewData);
      toast.success('Review submitted successfully!');
      setShowReviewModal(false);
      // Mark that review has been submitted
      setHasReview(true);
      // Reset review data
      setReviewData({ review: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <div className="approved-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="approved-content-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="approved-modal-header">
          <button className="approved-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="approved-modal-content">
          <div className="approved-modal-layout">
            {/* Left Side - Approved Content Info */}
            <div className="approved-left-panel">
              {/* Success Message */}
              <div className="approved-success-section">
                <div className="approved-success-badge">
                  <Sparkles size={16} />
                  <span>Approved</span>
                </div>
                <div className="approved-success-icon">
                  <div className="approved-success-icon-bg">
                    <CheckCircle size={56} />
                  </div>
                </div>
                <h2 className="approved-success-title">Content Approved</h2>
                <p className="approved-success-message">
                  {submission.creator.name} delivered exceptional content for {submission.campaign}
                </p>
                        <div className="approved-download-summary-section">
                          <h3 className="approved-section-title">
                            <Download size={20} />
                            Download Final Videos
                          </h3>
                          <button
                            className="approved-download-all-btn"
                            onClick={handleDownloadAll}
                          >
                            <Download size={14} />
                            Download All
                          </button>
                        </div>
              </div>

              {/* Review Section */}
              <div className="approved-review-section">
                {submissionHasReview ? (
                  // Show thank you message if review has been submitted
                  <div className="approved-review-thanks">
                    <h3 className="approved-section-title">
                      <Star size={20} />
                      Review Submitted
                    </h3>
                    <div className="approved-review-thanks-message">
                      <p>Thanks for leaving a review!</p>
                    </div>
                  </div>
                ) : (
                  // Show review prompt if no review yet
                  <>
                    <h3 className="approved-section-title">
                      <Star size={20} />
                      Leave Feedback
                    </h3>
                    
                    <div className="approved-review-prompt">
                      <p>Have something great to say? Give your thanks to the creator and leave a review.</p>
                      <button 
                        className="approved-leave-review-btn"
                        onClick={() => setShowReviewModal(true)}
                      >
                        <Star size={16} />
                        Leave Review
                      </button>
                    </div>
                  </>
                )}
                
                {onReassignCreator && (
                  <div className="approved-reassign-section">
                    <button
                      className="approved-reassign-creator-btn"
                      onClick={() => onReassignCreator(submission)}
                    >
                      <Users size={14} />
                      Re-assign Creator
                    </button>
                  </div>
                )}
              </div>
            </div>

                    {/* Right Side - Video Display */}
                    <div className="approved-right-panel">
                      <div className="approved-download-section">

                        <div className="approved-videos-container">
                          <div className="approved-video-item">
                            <div className="approved-video-brief-title">
                              <span className="approved-brief-name">
                                Brief: {submission?.briefName || submission?.briefTitle || submission?.brief?.name || submission?.brief?.title || 'Untitled'}
                              </span>
                            </div>
                            <div className="approved-video-title-top">
                              <span className="approved-video-title">
                                {(() => {
                                  // Try to get script name from various possible locations
                                  const scriptName = currentVideo?.scriptName || 
                                                   currentVideo?.scriptTitle || 
                                                   currentVideo?.script?.name || 
                                                   currentVideo?.script?.title ||
                                                   submission?.scripts?.[currentVideoIndex]?.name ||
                                                   submission?.scripts?.[currentVideoIndex]?.title ||
                                                   submission?.brief?.scripts?.[currentVideoIndex]?.name ||
                                                   submission?.brief?.scripts?.[currentVideoIndex]?.title ||
                                                   currentVideo?.title;
                                  
                                  // If we still have "Video 1" or similar, replace with script name
                                  if (scriptName && scriptName.includes('Video')) {
                                    return `Script ${currentVideoIndex + 1}`;
                                  }
                                  
                                  return scriptName || `Script ${currentVideoIndex + 1}`;
                                })()}
                              </span>
                            </div>
                            <div className="approved-video-preview">
                              <video 
                                src={currentVideo?.downloadURL || currentVideo?.videoUrl}
                                controls
                                className="approved-video-player"
                                poster={currentVideo?.thumbnailUrl}
                                key={currentVideoIndex}
                              >
                                Your browser does not support the video tag.
                              </video>
                            </div>
                            <div className="approved-video-info">
                              <div className="approved-video-nav-bottom">
                                <button 
                                  className="approved-video-nav-btn-bottom"
                                  onClick={handlePreviousVideo}
                                >
                                  <ChevronLeft size={16} />
                                </button>
                                <span className="approved-video-counter">
                                  {currentVideoIndex + 1} of {videos.length}
                                </span>
                                <button 
                                  className="approved-video-nav-btn-bottom"
                                  onClick={handleNextVideo}
                                >
                                  <ChevronRight size={16} />
                                </button>
                              </div>
                              <button 
                                className="approved-download-individual-btn"
                                onClick={() => handleDownloadIndividual(currentVideo)}
                              >
                                <Download size={14} />
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowReviewModal(false);
          }
        }}>
          <div className="review-modal" onClick={e => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2 className="review-modal-title">Leave a Review</h2>
              <button className="review-modal-close" onClick={() => setShowReviewModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="review-modal-content">
              <div className="review-modal-creator-info">
                <p>Share your experience working with <strong>{submission.creator.name}</strong></p>
              </div>
              
              <div className="review-modal-form">
                <textarea
                  value={reviewData.review}
                  onChange={(e) => setReviewData(prev => ({ ...prev, review: e.target.value }))}
                  placeholder="Tell us about your experience - communication, quality, timeliness, or anything else you'd like to share..."
                  rows={6}
                  className="review-textarea"
                />
              </div>
              
              <div className="review-modal-actions">
                <button 
                  className="review-cancel-btn"
                  onClick={() => setShowReviewModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="review-submit-btn" 
                  onClick={handleReviewSubmit}
                  disabled={isSubmittingReview || reviewData.review.trim() === ''}
                >
                  {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovedContentModal;
